import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useAddXP } from "@/hooks/useXPSystem";
import {
  MONEY_TRACK,
  MoneyCheckin,
  MoneyHabits,
  computeMoneyStats,
  liveFromMetrics,
  type LiveBalances,
  weekStart,
} from "@/lib/desk/foundations";
import { getPractice, updatePractice, isPracticeOn } from "@/lib/practice/store";
import { usePracticeMode } from "@/hooks/usePracticeMode";

// money_habits / money_checkins are staged in this draft; cast until types regenerate.
const db = supabase as any;

export interface MoneyData {
  checkins: MoneyCheckin[];
  habits: MoneyHabits | null;
  lessons: string[];
  override?: Record<string, number> | null;
  live?: LiveBalances | null;
}

export async function fetchMoneyData(uid: string, practice = false): Promise<MoneyData> {
  if (practice) {
    const p = getPractice();
    return { checkins: p.checkins, habits: p.habits, lessons: p.lessons };
  }
  const latestMetric = (metric: string) => db.from("metric_entries").select("value,details,recorded_at,created_at").eq("user_id", uid).eq("metric", metric).order("created_at", { ascending: false }).limit(1).maybeSingle();
  const [c, h, l, o, sv, dt] = await Promise.all([
    db.from("money_checkins").select("*").eq("user_id", uid).order("week_start", { ascending: true }),
    db.from("money_habits").select("*").eq("user_id", uid).maybeSingle(),
    supabase
      .from("learning_module_progress")
      .select("module_key")
      .eq("user_id", uid)
      .eq("track_id", MONEY_TRACK)
      .not("completed_at", "is", null),
    db.from("money_targets").select("targets").eq("user_id", uid).maybeSingle(),
    latestMetric("savings"),
    latestMetric("debt"),
  ]);
  const svr = sv?.error ? null : sv?.data, dtr = dt?.error ? null : dt?.data;
  return {
    checkins: (c.error ? [] : c.data ?? []) as MoneyCheckin[],
    habits: (h.error ? null : h.data) as MoneyHabits | null,
    lessons: ((l.data ?? []) as { module_key: string }[]).map((r) => r.module_key),
    override: o?.error ? null : (o?.data?.targets ?? null),
    live: liveFromMetrics(svr?.details ?? null, svr?.recorded_at ?? null, dtr ? Number(dtr.value) : null, dtr?.recorded_at ?? null),
  };
}

export function useMoneyFoundation() {
  const { user } = useAuth();
  const practice = usePracticeMode();
  const q = useQuery({
    queryKey: ["money-foundation", user?.id, practice],
    enabled: !!user,
    queryFn: () => fetchMoneyData(user!.id, practice),
  });
  const data = q.data ?? { checkins: [], habits: null, lessons: [] };
  return { ...data, stats: computeMoneyStats(data.checkins, data.habits, data.lessons, (data as MoneyData).override, (data as MoneyData).live), isLoading: q.isLoading };
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["money-foundation"] });
    qc.invalidateQueries({ queryKey: ["desk-career"] });
    qc.invalidateQueries({ queryKey: ["metric-entries"] });
  };
}

export function useSaveMoneyHabits() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const practice = usePracticeMode();
  return useMutation({
    mutationFn: async (h: MoneyHabits) => {
      if (practice) { updatePractice((d) => ({ ...d, habits: h })); return; }
      const { error } = await db
        .from("money_habits")
        .upsert({ user_id: user!.id, ...h, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Money setup saved."); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useMoneyCheckin() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const addXP = useAddXP();
  const practice = usePracticeMode();
  return useMutation({
    mutationFn: async (v: Omit<MoneyCheckin, "week_start"> & { wasMissedTwo: boolean }) => {
      const { wasMissedTwo, ...row } = v;
      const week = weekStart();
      if (practice) {
        updatePractice((d) => ({
          ...d,
          checkins: [...d.checkins.filter((c) => c.week_start !== week), { ...row, week_start: week } as MoneyCheckin],
        }));
        return;
      }
      const { error } = await db
        .from("money_checkins")
        .upsert({ user_id: user!.id, week_start: week, ...row }, { onConflict: "user_id,week_start" });
      if (error) throw error;

      // Keep Kingdom Hub numbers (trajectory, stage gates) in step.
      const now = new Date().toISOString();
      const metrics: { metric: string; value: number }[] = [
        { metric: "savings", value: row.savings },
        { metric: "debt", value: row.total_debt },
      ];
      if (row.fico != null) metrics.push({ metric: "fico", value: row.fico });
      await db.from("metric_entries").insert(
        metrics.map((m) => ({ user_id: user!.id, ...m, details: { source: "money_checkin" }, is_baseline: false, recorded_at: now })),
      );

      if (wasMissedTwo) {
        await db.from("off_track_events").insert({ user_id: user!.id, off_track: false, reason: "money check-in resumed after 2+ missed weeks" });
      }
      try { await addXP.mutateAsync({ amount: 25, actionType: "money_checkin", description: "Weekly money check-in" }); } catch { /* XP optional */ }
    },
    onSuccess: () => { invalidate(); toast.success("Check-in saved. +25 XP"); },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCompleteMoneyLesson() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const practice = usePracticeMode();
  return useMutation({
    mutationFn: async (lessonId: string) => {
      if (practice) { updatePractice((d) => ({ ...d, lessons: [...new Set([...d.lessons, lessonId])] })); return; }
      const { error } = await supabase.from("learning_module_progress").upsert(
        { user_id: user!.id, track_id: MONEY_TRACK, module_key: lessonId, completed_at: new Date().toISOString(), quiz_score: 100 },
        { onConflict: "user_id,track_id,module_key" },
      );
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast.success("Lesson complete."); },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** Records the Off Track moment once when two weeks in a row are missed. */
export async function logMoneyOffTrack(uid: string) {
  if (isPracticeOn()) return;
  const key = `money-offtrack-${uid}-${weekStart()}`;
  if (localStorage.getItem(key)) return;
  localStorage.setItem(key, "1");
  await db.from("off_track_events").insert({ user_id: uid, off_track: true, reason: "missed 2 weekly money check-ins in a row" });
}
